import { JOB_ORDER_DRAWERS, JOB_ORDER_PROFILE_CONFIG } from 'features/inventory-profiles/constants';
import { formatLocation } from 'features/inventory-profiles/utils';
import strings from 'strings';

const COPIES = strings.inventoryProfiles;

/**
 * Source params of PresentationMakerTemplate for a job order.
 * @param {Object} jobOrderData Job order profile
 * @returns {Object | null}
 */
export const buildPresentationMakerJobOrderParam = jobOrderData => {
  if (!jobOrderData?.id) return null;

  const { address, id, title, specialty, subspecialty, company } = jobOrderData;

  return {
    jobOrder: {
      city: address?.city?.title,
      companyId: company?.id,
      companyName: company?.name,
      id,
      industry: specialty?.industry?.title,
      postalCode: address?.zip,
      specialty: specialty?.title,
      state: address?.city?.state?.title,
      subspecialty: subspecialty?.title,
      title
    }
  };
};

const findJobOrderType = ({ jobOrder, dataSheet, catalogs }) => {
  const typeId = dataSheet?.data?.section9?.jobOrderType;
  const fromCatalog = catalogs?.jobOrderTypes?.data?.find(type => type.id === typeId);
  return fromCatalog ?? jobOrder.whiteSheet?.jobOrderType;
};

/**
 * The only code that knows the Job Order API shape. Pure: no rendering, no hooks.
 * @param {Object} params
 * @param {Object} params.jobOrder Job order from useJobOrderProfile, with its data sheet status
 * @param {Object} [params.dataSheet] Job order data sheet
 * @param {Object|null} [params.catalogs] Catalogs of useFetchCatalogs (null until they load)
 * @returns {{ header: Object, summary: Object, status: Object }}
 */
export const formatJobOrderResponse = ({ jobOrder, dataSheet, catalogs }) => ({
  header: {
    entityType: JOB_ORDER_PROFILE_CONFIG.type,
    title: jobOrder.title,
    lastActivity: jobOrder.last_activity_date
  },
  summary: {
    company: { id: jobOrder.company?.id, name: jobOrder.company?.name },
    location: formatLocation(jobOrder.address?.city),
    created: {
      date: jobOrder.created_at,
      by: jobOrder.createdBy?.personalInformation?.full_name
    },
    details: []
  },
  status: {
    inventoryType: findJobOrderType({ jobOrder, dataSheet, catalogs }),
    itemStatus: jobOrder.status,
    subStatus: jobOrder.subStatus,
    entityTypeId: JOB_ORDER_PROFILE_CONFIG.id
  }
});

/**
 * Job Order ⋮ menu, in V1 order: edit, data sheet, verify urgency, recruiters, collaborator,
 * own items, claim.
 * @param {Object} params
 * @param {Object} params.permissions Result of useJobOrderPermissions
 * @param {boolean} params.canManage From useProfileAccountability
 * @param {boolean} params.canView From useProfileAccountability
 * @param {boolean} params.canCollaborate From useProfileAccountability
 * @param {boolean} params.hasAssistant From useProfileAccountability: switches the collaborator label
 * @param {(name: string) => () => void} params.openDrawer From useProfileDrawer
 * @param {() => void} params.onOpenDataSheet `openDataSheet` of useJobOrderActions
 * @param {() => void} params.onDuplicate `duplicate` of useJobOrderActions
 * @returns {Array<{ title: string, action: () => void, visible: boolean }>}
 */
export const buildJobOrderMenuItems = ({
  canCollaborate,
  canManage,
  canView,
  hasAssistant,
  onDuplicate,
  onOpenDataSheet,
  openDrawer,
  permissions
}) => [
  {
    title: COPIES.common.editProfile,
    action: openDrawer(JOB_ORDER_DRAWERS.Edit),
    visible: permissions.canEdit
  },
  {
    title: strings.inventory.jobOrders.profile.editButton,
    action: onOpenDataSheet,
    visible: permissions.canEdit
  },
  {
    title: COPIES.menu.verifyUrgency,
    action: openDrawer(JOB_ORDER_DRAWERS.VerifyUrgency),
    visible: permissions.canVerifyUrgency
  },
  {
    title: canManage ? COPIES.menu.manageRecruiters : COPIES.menu.viewRecruiters,
    action: openDrawer(JOB_ORDER_DRAWERS.Recruiters),
    visible: canManage || canView
  },
  {
    title: hasAssistant ? COPIES.menu.manageCollaborator : COPIES.menu.addCollaborator,
    action: openDrawer(JOB_ORDER_DRAWERS.Recruiters),
    visible: canCollaborate
  },
  { title: COPIES.menu.duplicate, action: onDuplicate, visible: permissions.canEdit },
  {
    title: COPIES.menu.addAction,
    action: openDrawer(JOB_ORDER_DRAWERS.Task),
    visible: permissions.canUseDailyPlan
  },
  {
    title: strings.ai.generators.roleplay.actionButton,
    action: openDrawer(JOB_ORDER_DRAWERS.Roleplay),
    visible: permissions.canUseRoleplay
  },
  {
    title: COPIES.accountability.action,
    action: openDrawer(JOB_ORDER_DRAWERS.Claim),
    visible: permissions.canClaimAccountability
  }
];
